import { useEffect, useMemo, useState } from "react";
import { MapPin, CalendarDays, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { EventDialog } from "@/components/EventDialog";
import { fmtDate, timeRange, todayStr, mapLink, canSignUp, fmtPrice } from "@/lib/events";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function groupByCity(events) {
  const today = todayStr();
  const cities = {};
  events.forEach((e) => {
    const city = e.city || "Other";
    const c = (cities[city] ||= { city, venues: {}, upcoming: [], last: "" });
    if (e.location_name) c.venues[e.location_name] = e;
    if (e.date >= today) c.upcoming.push(e);
    if (e.date > c.last) c.last = e.date;
  });
  return Object.values(cities).sort((a, b) => (b.upcoming.length > 0) - (a.upcoming.length > 0) || b.last.localeCompare(a.last));
}

function CityCard({ c, onPick }) {
  return (
    <div className="bg-white rounded-2xl border border-[#dfd8da] p-6 flex flex-col" data-testid={`location-${slug(c.city)}`}>
      <h3 className="text-xl font-bold text-[#550001]">{c.city}, OH</h3>
      <ul className="mt-3 space-y-1.5">
        {Object.values(c.venues).map((v) => (
          <li key={v.location_name} className="flex items-start gap-2 text-sm text-[#1a1617]">
            <MapPin size={15} className="mt-0.5 text-[#920e12] shrink-0" aria-hidden="true" />
            <span><strong>{v.location_name}</strong>{v.address && <> · <a href={mapLink(v)} target="_blank" rel="noreferrer" className="underline hover:text-[#920e12]">{v.address}</a></>}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-4 border-t border-[#eee5e6] flex-1">
        {c.upcoming.length ? (
          <ul className="space-y-2">
            {c.upcoming.slice(0, 4).map((e) => (
              <li key={e.id}>
                <button onClick={() => onPick(e)} data-testid={`location-event-${e.id}`}
                  className="w-full text-left flex items-start gap-2 text-sm hover:text-[#920e12] transition-colors duration-150">
                  <CalendarDays size={15} className="mt-0.5 text-[#920e12] shrink-0" aria-hidden="true" />
                  <span><strong>{fmtDate(e.date, { weekday: "short", month: "short", day: "numeric" })}</strong>{timeRange(e) && ` · ${timeRange(e)}`} · {e.title}
                    {canSignUp(e) && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#920e12] text-white text-[10px] font-bold">{fmtPrice(e.price_cents)}</span>}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[#545454]" data-testid={`location-none-${slug(c.city)}`}>No upcoming dates yet. New sessions are added often.</p>
        )}
      </div>
    </div>
  );
}

export function ProgramLocations() {
  const [events, setEvents] = useState([]);
  const [picked, setPicked] = useState(null);
  useEffect(() => { api.get("/events").then(({ data }) => setEvents(data)).catch(() => {}); }, []);
  const cities = useMemo(() => groupByCity(events), [events]);
  if (!cities.length) return null;
  return (
    <section className="mt-20" aria-labelledby="locations-heading" data-testid="program-locations">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Where we meet</span>
          <h2 id="locations-heading" className="mt-1 text-3xl font-bold text-[#1a1617]">Program locations</h2>
        </div>
        <Link to="/calendar" className="inline-flex items-center gap-1 font-bold text-[#920e12] hover:underline" data-testid="locations-calendar-link">
          Full calendar <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {cities.map((c) => <CityCard key={c.city} c={c} onPick={setPicked} />)}
      </div>
      <EventDialog event={picked} onClose={() => setPicked(null)} />
    </section>
  );
}
